import React, { useState, useMemo } from "react";
import { useSkinsMasterContext } from "./SkinsMaster.jsx";
import { useMySkinsContext } from "./MySkins.jsx";
import { useQueries } from "@tanstack/react-query";
import { q_skins_master_applied, q_skinrelease, q_skinapply } from "@/queries/queries.jsx";
import { getv } from "../../utils/utils.jsx";
import { ElementIcon, GenderIcon } from "../../utils/cn_map2.jsx";
import { Flame } from "lucide-react";
import { Modal } from "../../components/Modal.jsx";
import {
  gets_skinurl,
  rarity_colors,
  get_primary_color,
  get_accent_color,
} from "./dependencies.jsx";
import { BurnSkinModal } from "./BurnSkin.jsx";
import { Skin3D_Preview } from "./Skin3D_Preview.jsx";
import _ from "lodash";

export const MySkinControlCenter = () => {
  const skm_con = useSkinsMasterContext();
  const myskins_con = useMySkinsContext();
  const [view_mode, set_view_mode] = useState("image"); // "image" or "3d"
  const [removing_rvsids, set_removing_rvsids] = useState(new Set());

  // Apply modal state
  const [apply_modal_open, set_apply_modal_open] = useState(false);
  const [apply_stage, set_apply_stage] = useState(1); // 1: core selection, 2: confirmation
  const [selected_core, set_selected_core] = useState(null);
  const [current_apply_rvsid, set_current_apply_rvsid] = useState(null);
  const [applying, set_applying] = useState(false);

  const { owned_skins, skin_infomap, vault, cores_map, post_burn_function } = skm_con;
  const { selected_skin, set_selected_skin } = myskins_con;

  // Get current rvsids - use empty array if no selected skin
  const current_rvsids = selected_skin?.rvsids || [];

  // Fetch applied data specifically for current skin instances
  const [qo_skins_applied] = useQueries({
    queries: [
      q_skins_master_applied(
        { rvsids: current_rvsids },
        { enabled: current_rvsids.length > 0 },
      ),
    ],
  });

  const [local_applied_map, local_hmap] = useMemo(() => {
    let d = getv(qo_skins_applied, "data.result");
    let applied_map = getv(d, "applied_map", {});
    let hmap = getv(d, "hmap", {});
    return [applied_map, hmap];
  }, [qo_skins_applied.dataUpdatedAt]);

  // Refresh applied skins data
  const refresh_skins_applied = () => {
    setTimeout(() => {
      qo_skins_applied.refetch();
    }, 2000); // 2 second delay
  };

  // Handle remove skin action
  const handle_remove_skin = async (rvsid) => {
    try {
      // Add to removing set for loading state
      set_removing_rvsids(prev => new Set([...prev, rvsid]));

      // Parse rvsid to get rvmode and skinid
      const parts = rvsid.split('-');
      const rvmode = parts[0]; // First part
      const skinid = parseInt(parts[1]); // Second part as integer

      // Call release API with proper body params
      const releaseQuery = q_skinrelease({ skinid, rvmode });
      await releaseQuery.queryFn();

      // Refresh applied skins after 2 seconds
      refresh_skins_applied();

      // Remove from loading state after 3 seconds (1 sec after refresh)
      setTimeout(() => {
        set_removing_rvsids(prev => {
          const newSet = new Set(prev);
          newSet.delete(rvsid);
          return newSet;
        });
      }, 3000);

    } catch (error) {
      console.error("Failed to remove skin:", error);
      // Remove from loading state on error
      set_removing_rvsids(prev => {
        const newSet = new Set(prev);
        newSet.delete(rvsid);
        return newSet;
      });
    }
  };

  // Handle apply skin modal
  const handle_apply_skin = (rvsid) => {
    set_current_apply_rvsid(rvsid);
    set_apply_stage(1);
    set_selected_core(null);
    set_apply_modal_open(true);
  };

  const handle_core_select = (core) => {
    set_selected_core(core);
    set_apply_stage(2);
  };

  const handle_apply_confirm = async () => {
    if (!selected_core || !current_apply_rvsid) return;

    try {
      set_applying(true);

      // Parse rvsid to get rvmode and skinid
      const parts = current_apply_rvsid.split('-');
      const rvmode = parts[0];
      const skinid = parseInt(parts[1]);

      // Call apply API
      const applyQuery = q_skinapply({
        skinid,
        rvmode,
        hid: selected_core.hid
      });
      await applyQuery.queryFn();

      // Refresh applied skins after 2 seconds
      refresh_skins_applied();

      // Close modal after 3 seconds
      setTimeout(() => {
        set_applying(false);
        set_apply_modal_open(false);
        set_selected_core(null);
        set_current_apply_rvsid(null);
        set_apply_stage(1);
      }, 3000);

    } catch (error) {
      console.error("Failed to apply skin:", error);
      set_applying(false);
    }
  };

  const close_apply_modal = () => {
    if (!applying) {
      set_apply_modal_open(false);
      set_selected_core(null);
      set_current_apply_rvsid(null);
      set_apply_stage(1);
    }
  };

  // Get cores list for modal
  const cores_list = useMemo(() => {
    return _.values(cores_map);
  }, [cores_map]);

  // Early return after hooks
  if (!selected_skin) return null;

  const { infoid, baseskinid, info, rvmode, skins_n, rvsids } = selected_skin;
  const skinImageUrl = gets_skinurl(info.skin, rvmode, info?.is_new);

  const rarity = info?.rarity || "common";
  const rarity_color = rarity_colors[rarity] || rarity_colors.common;
  const primary_color = get_primary_color(info?.skin || baseskinid);
  const accent_color = get_accent_color(info?.skin || baseskinid);
  const skin_name = info?.skin || baseskinid;
  const is_new = info?.is_new || false;

  // Get individual skin instances for this infoid
  const skin_instances = owned_skins.filter((skin) => skin.infoid === infoid);

  const handle_back = () => {
    set_selected_skin(null);
  };

  return (
    <div className="w-full h-full">
      {/* Header Row - Back Button */}
      <div className="flex items-center mb-6">
        <button
          onClick={handle_back}
          className="flex items-center space-x-2 px-4 py-2 bg-slate-600/40 hover:bg-slate-500/50 rounded-lg text-white transition-all duration-200 backdrop-blur-md border border-slate-500/30"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span className="font-medium">Back to List</span>
        </button>
      </div>

      {/* Full Width Info Row */}
      <div
        className="mb-8 p-4 sm:p-6 rounded-xl backdrop-blur-md border border-slate-500/30"
        style={{
          background: `linear-gradient(135deg, ${primary_color}08, ${primary_color}04)`,
        }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Left: Skin Name and Tags */}
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 font-digi">
              {skin_name}
            </h1>
            <div className="flex items-center flex-wrap gap-2 sm:gap-3">
              {/* Rarity Badge */}
              <div
                className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm font-bold text-white shadow-sm"
                style={{ backgroundColor: rarity_color }}
              >
                {rarity.toUpperCase()}
              </div>

              {/* RVMode Badge */}
              <div className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium bg-slate-700/60 text-white">
                {rvmode.toUpperCase()}
              </div>
            </div>
          </div>

          {/* Right: Copies Owned */}
          <div className="text-left md:text-right">
            <div className="text-4xl sm:text-5xl md:text-6xl font-bold font-digi text-white">
              x{skins_n}
            </div>
            <div className="text-xs sm:text-sm text-gray-400 mt-1">
              {skins_n === 1 ? "Copy Owned" : "Copies Owned"}
            </div>
          </div>
        </div>
      </div>

      {/* 50/50 Split Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:h-[600px]">
        {/* Left Panel - Individual RVSIDs */}
        <div className="space-y-3 sm:space-y-4 overflow-y-auto max-h-[400px] lg:max-h-none">
          <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-4 font-digi">
            Skin NFTs
          </h3>

          {skin_instances.map((instance, index) => {
            const is_applied = local_applied_map[instance.rvsid];
            // Use cores_map from context, fallback to local_hmap
            const hero_info = is_applied
              ? cores_map[is_applied] || local_hmap[is_applied]
              : null;
            const is_removing = removing_rvsids.has(instance.rvsid);

            return (
              <div
                key={instance.rvsid}
                className="p-2.5 sm:p-3 rounded-lg backdrop-blur-md border border-slate-500/30 bg-slate-600/20 hover:bg-slate-500/30 transition-all duration-200"
              >
                {/* Top Row: RVSID + Buttons */}
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="text-white font-bold text-sm sm:text-base md:text-lg truncate">
                    #{instance.rvsid}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-1.5 sm:space-x-2 flex-shrink-0">
                    {is_applied ? (
                      <button
                        onClick={() => handle_remove_skin(instance.rvsid)}
                        disabled={is_removing}
                        className={`flex items-center space-x-1 px-2 sm:px-2.5 py-1 text-white text-[10px] sm:text-xs rounded-md transition-all duration-200 ${
                          is_removing
                            ? "bg-red-400/50 cursor-not-allowed"
                            : "bg-red-600/70 hover:bg-red-600/90"
                        }`}
                      >
                        {is_removing ? (
                          <>
                            <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span className="hidden sm:inline">Removing...</span>
                          </>
                        ) : (
                          <span>Remove</span>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => handle_apply_skin(instance.rvsid)}
                        className="px-2 sm:px-2.5 py-1 bg-green-600/70 hover:bg-green-600/90 text-white text-[10px] sm:text-xs rounded-md transition-all duration-200"
                      >
                        Apply
                      </button>
                    )}

                    {/* Burn Button with Modal */}
                    <BurnSkinModal
                      rvsid={instance.rvsid}
                      skin_name={skin_name}
                      skin_rarity={rarity}
                      applied_map={local_applied_map}
                      on_remove_skin={handle_remove_skin}
                      post_burn_function={post_burn_function}
                      on_close={() => refresh_skins_applied()}
                    />
                  </div>
                </div>

                {/* Applied Core Info */}
                {is_applied && hero_info ? (
                  <div>
                    <div className="text-green-400 text-xs sm:text-sm font-medium mb-1 truncate">
                      #{is_applied} - {hero_info.name}
                    </div>
                    <div className="flex items-center flex-wrap gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-300">
                      <span className="font-medium text-white">
                        {hero_info.type?.slice(0, 3).toUpperCase()}
                      </span>
                      <ElementIcon element={hero_info.element} size="w-3 h-3" />
                      <GenderIcon gender={hero_info.gender} size="w-3 h-3" />
                      <span className="text-gray-400">F{hero_info.fno}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-400 text-[10px] sm:text-xs">
                    Not applied to any Core
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right Panel - Preview */}
        <div className="space-y-4 sm:space-y-6">
          {/* Preview Panel with Mode Toggle */}
          <div className="rounded-xl overflow-hidden backdrop-blur-md border border-slate-500/30">
            {/* Header with View Mode Toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 border-b border-slate-500/30">
              <h3 className="text-lg sm:text-xl font-bold text-white font-digi">
                Skin Preview
              </h3>

              {/* View Mode Button Group - Only show if skin is new */}
              {is_new ? (
                <div className="flex bg-slate-700/40 rounded-lg p-1">
                  <button
                    onClick={() => set_view_mode("image")}
                    className={`flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md transition-all duration-200 ${
                      view_mode === "image"
                        ? "bg-blue-600 text-white shadow-md"
                        : "text-gray-300 hover:text-white hover:bg-slate-600/50"
                    }`}
                  >
                    {/* Image Icon */}
                    <svg
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="text-xs sm:text-sm font-medium">Image</span>
                  </button>

                  <button
                    onClick={() => set_view_mode("3d")}
                    className={`flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md transition-all duration-200 ${
                      view_mode === "3d"
                        ? "bg-purple-600 text-white shadow-md"
                        : "text-gray-300 hover:text-white hover:bg-slate-600/50"
                    }`}
                  >
                    {/* 3D Cube Icon */}
                    <svg
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                    <span className="text-xs sm:text-sm font-medium">3D</span>
                  </button>
                </div>
              ) : (
                <div className="px-3 py-1.5 bg-slate-700/40 rounded-lg">
                  <span className="text-xs sm:text-sm font-medium text-gray-300">Image Only</span>
                </div>
              )}
            </div>

            {/* Content Area */}
            <div className="p-3 sm:p-4">
              {!is_new || view_mode === "image" ? (
                // Image View
                <div className="aspect-[1166/656] relative">
                  {skinImageUrl ? (
                    <img
                      src={skinImageUrl}
                      alt={skin_name}
                      className="w-full h-full object-cover rounded-lg shadow-lg"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-slate-600/60 to-slate-800/80 flex items-center justify-center rounded-lg">
                      <span className="text-gray-400 text-sm sm:text-base md:text-lg font-medium">
                        No Image Available
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                // 3D Model View
                <div className="aspect-[1166/656] relative">
                  <Skin3D_Preview skin_name={skin_name} rvmode={rvmode} />
                </div>
              )}
            </div>
          </div>

          {/* Legacy Skin Warning */}
          {!is_new && (
            <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4 backdrop-blur-md">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-yellow-500 mb-1">Legacy Skin</h4>
                  <p className="text-xs text-gray-300">
                    This is a skin compatible with older 3D cores. We advise you to burn, encash SGC and use it to purchase new exciting skins.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Apply Skin Modal */}
      <Modal
        is_open={apply_modal_open}
        on_close={close_apply_modal}
        title={apply_stage === 1 ? "Select Core" : "Confirm Application"}
        size="lg"
        backdrop_close={!applying}
      >
        {apply_stage === 1 ? (
          // Stage 1: Core Selection
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-white mb-2">Choose a core to apply the skin to:</h3>
              <p className="text-gray-400 text-sm">Select from your available cores</p>
            </div>

            <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
              {cores_list.map((core) => (
                <button
                  key={core.hid}
                  onClick={() => handle_core_select(core)}
                  className="p-4 rounded-lg border border-slate-600/50 hover:border-slate-500 bg-slate-700/30 hover:bg-slate-600/40 transition-all duration-200 text-left"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium">#{core.hid} - {core.name}</div>
                      <div className="flex items-center space-x-2 text-sm text-gray-300 mt-1">
                        <span className="font-medium text-white">
                          {core.type?.slice(0, 3).toUpperCase()}
                        </span>
                        <ElementIcon element={core.element} size="w-4 h-4" />
                        <GenderIcon gender={core.gender} size="w-4 h-4" />
                        <span className="text-gray-400">F{core.fno}</span>
                      </div>
                    </div>
                    <div className="text-blue-400">
                      Select →
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          // Stage 2: Confirmation
          <div className="p-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-white mb-2">Apply Skin Confirmation</h3>
              <p className="text-gray-400">Review the details below and confirm the application</p>
            </div>

            {/* Skin Info */}
            <div className="bg-slate-700/30 rounded-lg p-4 mb-6">
              <h4 className="text-white font-medium mb-2">Skin Details</h4>
              <div className="flex items-center space-x-3">
                <div
                  className="px-2 py-1 rounded text-xs font-bold text-white"
                  style={{ backgroundColor: rarity_color }}
                >
                  {rarity.toUpperCase()}
                </div>
                <span className="text-white font-medium">{skin_name}</span>
              </div>
            </div>

            {/* Core Info */}
            <div className="bg-slate-700/30 rounded-lg p-4 mb-6">
              <h4 className="text-white font-medium mb-2">Target Core</h4>
              <div>
                <div className="text-white font-medium">#{selected_core?.hid} - {selected_core?.name}</div>
                <div className="flex items-center space-x-2 text-sm text-gray-300 mt-1">
                  <span className="font-medium text-white">
                    {selected_core?.type?.slice(0, 3).toUpperCase()}
                  </span>
                  <ElementIcon element={selected_core?.element} size="w-4 h-4" />
                  <GenderIcon gender={selected_core?.gender} size="w-4 h-4" />
                  <span className="text-gray-400">F{selected_core?.fno}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={() => set_apply_stage(1)}
                disabled={applying}
                className="flex-1 px-4 py-2 bg-gray-600/70 hover:bg-gray-600/90 disabled:bg-gray-600/40 disabled:cursor-not-allowed text-white rounded-md transition-all duration-200"
              >
                Back
              </button>
              <button
                onClick={handle_apply_confirm}
                disabled={applying}
                className="flex-1 px-4 py-2 bg-green-600/70 hover:bg-green-600/90 disabled:bg-green-400/50 disabled:cursor-not-allowed text-white rounded-md transition-all duration-200 flex items-center justify-center space-x-2"
              >
                {applying ? (
                  <>
                    <div className="w-4 h-4 border border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Applying...</span>
                  </>
                ) : (
                  <span>Apply Skin</span>
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
